/**
 * Shared "company setup" field group — company name (+ randomize), company
 * colour swatches, starting port — rendered/bound identically wherever it's
 * needed: the registration form (src/ui/modals/AuthModal.js) and the
 * "reset my game" flow (src/ui/modals/GameMenuModal.js, pre-filled with the
 * player's current values). Not a Modal itself — just an HTML fragment plus
 * a bind function, since both call sites embed it inside their OWN modal.
 */
import { COMPANY_COLOR_PALETTE, COMPANY_NAME_MAX_LENGTH } from '../../config/constants.js';
import { STARTING_PORT_IDS } from '../../config/ports.js';
import { pickCompanyName } from '../../config/companyNames.js';
import { escapeHtml } from '../../utils/formatting.js';
import { GameState } from '../../core/GameState.js';

/**
 * @param {string} idPrefix  Unique per call site (e.g. 'auth-setup', 'gmm-reset') so
 *   IDs never collide if both happen to exist in the DOM at once.
 * @param {{ companyName: string, companyColor: string, startPortId: string }} values
 * @returns {string} HTML fragment
 */
export function companySetupFieldsHtml(idPrefix, { companyName, companyColor, startPortId }) {
  const portOptions = STARTING_PORT_IDS.map(id => {
    const port = GameState.getPort(id);
    const label = port ? `${port.name}, ${port.country}` : id;
    return `<option value="${id}" ${id === startPortId ? 'selected' : ''}>${escapeHtml(label)}</option>`;
  }).join('');

  return `
    <div class="company-setup-fields">
      <div class="route-field">
        <label>Company name</label>
        <div class="company-name-row">
          <input type="text" id="${idPrefix}-name" value="${escapeHtml(companyName)}" maxlength="${COMPANY_NAME_MAX_LENGTH}">
          <button type="button" class="btn btn-sm company-name-random" id="${idPrefix}-name-random" title="Randomize">🎲</button>
        </div>
      </div>
      <div class="menu-color-picker">
        <span class="menu-color-picker-label">Company color</span>
        <div class="menu-color-swatches" id="${idPrefix}-swatches">
          ${COMPANY_COLOR_PALETTE.map(c => `
            <button type="button" class="menu-color-swatch${c === companyColor ? ' active' : ''}"
                    data-color="${c}" style="background:${c}" title="${c}"></button>`).join('')}
        </div>
      </div>
      <div class="route-field">
        <label>Starting port</label>
        <select id="${idPrefix}-port">${portOptions}</select>
      </div>
    </div>`;
}

/**
 * Wire up the fragment rendered by companySetupFieldsHtml(). Mutates `state`
 * in place as the player interacts — the caller reads state.companyName/
 * companyColor/startPortId whenever it submits (no need for a return value
 * or extra callbacks).
 *
 * @param {string} idPrefix  Same prefix passed to companySetupFieldsHtml().
 * @param {ParentNode} root  Element containing the rendered fragment (e.g. the modal's root).
 * @param {{ companyName: string, companyColor: string, startPortId: string }} state
 */
export function bindCompanySetupFields(idPrefix, root, state) {
  const nameInput = root.querySelector(`#${idPrefix}-name`);
  nameInput.addEventListener('input', (e) => { state.companyName = e.target.value; });

  // mousedown + preventDefault (not click) so the button never steals focus
  // from the input first — same pattern as FleetPanel.js's ship-name
  // randomizer button.
  root.querySelector(`#${idPrefix}-name-random`).addEventListener('mousedown', (e) => {
    e.preventDefault();
    const name = pickCompanyName();
    nameInput.value = name;
    state.companyName = name;
  });

  root.querySelectorAll(`#${idPrefix}-swatches .menu-color-swatch`).forEach(btn => {
    btn.addEventListener('click', () => {
      state.companyColor = btn.dataset.color;
      root.querySelectorAll(`#${idPrefix}-swatches .menu-color-swatch`).forEach(b => b.classList.toggle('active', b === btn));
    });
  });

  root.querySelector(`#${idPrefix}-port`).addEventListener('change', (e) => { state.startPortId = e.target.value; });
}
